import React, { useEffect, useState } from 'react';
import { useSubscription } from '@apollo/react-hooks';
import {
  CancelPolicyAccordionBody,
  CancelPolicyAccordionHead,
  CancelPolicyAccordionWrap,
  ConnectButton,
  HeaderChevronWrap,
  HeaderIcon,
  HeaderIconWrap,
  HeaderTitle,
  HeaderTitleWrap,
  SuccessText
} from './gpaCancelPolicyStyles';
import {
  ChevronUp,
  ChevronDownUnbolded,
  CancelPolicyDisabled,
  ChevronDownDisabled,
  AltSuccess
} from '../../icons';
import CancelPolicyIconSrc from '../../assets/images/CancelPolicy.svg';
import CancelPolicyForm from '../gpaCancelPolicy/gpaCancelPolicyForm';
import { CANCEL_POLICY_cancelPolicy } from '../cancelPolicy/graphql/__generated__/CANCEL_POLICY';
import { WarningText } from '../cancelPolicy/cancelPolicyForm/cancelPolicyFormStyles';
import CancellationRequestSent from '../cancelPolicy/cancellationRequestSent/cancellationRequestSent';
import CancelPolicyInfo from './CancelPolicyInfo';
import { ApolloError } from 'apollo-client';
import AlertModal from './InfoAlertModal';
import AlertLoader from './AlertLoader';
import { loader } from 'graphql.macro';
import { convertToUtc } from '../../util/dateConvertToUTC';

const on_process_result = loader('./graphql/Run_Subscription_Result.graphql');

export interface CancelPolicyAccordionHeadProps {
  disabled: boolean;
}

export interface GpaCancelPolicyProps {
  result: any;
  upload: Function;
  uploadError?: Error;
  isCancelled: boolean;
  scheduledCancellation: boolean;
  cancel: Function;
  cancelData: any;
  cancelError?: Error;
  policyNumber: string;
  systemCode: string;
  minDate: string;
  maxDate: string;
  product: string;
  getAcceptance: Function;
  acceptable: boolean;
  openChanges: boolean;
  accountNumber: string;
  acceptanceLoading: boolean;
  agencyBill: boolean;
  environment: string;
  sourceSystemCode: string;
  openCancellation: boolean;
  confirmClickHandler: Function;
  config: any;
  setIsUpdated: Function;
  uploadToken: string;
  setStartReload: Function;
  getRefundMethodClickHandler: Function;
  refundMethods: string[] | [];
  showCancelPolicyFlag: boolean;
  isFarmRanchLine: boolean;
}

export interface formValues {
  cancellationReason: string;
  effectiveDate: string;
  cancellationDocument: File;
  securityType: string;
  reasonCode: string;
  uploadAvailable: boolean;
  carrierMovedTo: string;
  sourceSystemCode: string;
  otherCarrier: string;
  refundMethod: string;
}

export const formStatusObj = {
  notSubmitted: 'notSubmitted',
  submitting: 'submitting',
  submitted: 'submitted'
};

export default ({
  result, upload, uploadError, isCancelled, scheduledCancellation, cancel, cancelData, cancelError, policyNumber, systemCode, maxDate, minDate, product, agencyBill
  , openChanges, getAcceptance, acceptable, acceptanceLoading, accountNumber, environment, sourceSystemCode, openCancellation, confirmClickHandler, config, setIsUpdated,
  uploadToken, setStartReload, getRefundMethodClickHandler,
  refundMethods, showCancelPolicyFlag, isFarmRanchLine
}: GpaCancelPolicyProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formStatus, setFormStatus] = useState(formStatusObj.notSubmitted); // Initialize with formStatusObj.submitted if user has submitted the form already
  const [showPolicyInfoModel, setShowPolicyInfoModel] = useState<boolean>(openCancellation);
  const [cancelDataUpdate, setCancelDataUpdate] = useState<boolean>(false);
  const [isCancelledSuccess, setIsCancelledSuccess] = useState<boolean>(false);
  const [isCancellationStarted, setIsCancellationStarted] = useState<boolean>(false);
  const cppProducts = ['General Liability', 'COMMERCIAL PACKAGE', 'Inland Marine'];
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [runSubscription, setRunSubscription] = useState<string | null>(null);

  const { data: subscriptionData, error: subscriptionError, loading: subLoading } = useSubscription(
    on_process_result,
    {
      skip: !runSubscription,
      variables: {
        policy_number: policyNumber,
      },
    }
  );

  let initialSource = 'Select Requested By';
  if (product !== 'COMMERCIAL PACKAGE' && product !== 'WORKERS COMP' && product !== 'Inland Marine') {
    initialSource = agencyBill ? 'Agent' : 'Insured';
  }
  const [cancellationSource, setCancellationSource] = useState<string>(
    initialSource
  );

  const getRefundMethodsForCPP = ()=>{
    // This function is for LOBs where refundMethod to be fetched on Opening the Cancel Form
    if(product === 'Businessowners Line' || product === 'Commercial Umbrella Line' || product === 'Commercial Auto Line (v7)' || !!isFarmRanchLine){
      let defaultCode = "fincononpay";
      getRefundMethodClickHandler({
        variables: {
          policy_number: policyNumber,
          query_env: environment,
          source_system_code: sourceSystemCode,
          getRefundMethods: true,
          cancellationSource: "insured",
          cancelReasonCode: defaultCode,
        }
      });
    }
  };

  const translateDocument = async (f: Blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(f);
      reader.onload = () => resolve(reader.result);
      reader.onerror = err => {
        reject(err);
        return null;
      };
    });

  const submitForm = async ({
    cancellationReason,
    effectiveDate,
    cancellationDocument,
    securityType,
    reasonCode,
    uploadAvailable,
    carrierMovedTo,
    sourceSystemCode,
    otherCarrier
  }: formValues) => {
    if (acceptable) {
      setFormStatus(formStatusObj.submitting);
      let isCppProduct = false;
      let isWorkerCompLine = false;
      let method = "createPolicyCancellationTransaction";
      try {
        if (cppProducts.includes(product)) {
          isCppProduct = true;
          method = "createPolicyCancellationTransactionCPP_SA";
        }
        if (product === "WORKERS COMP"){
          isWorkerCompLine = true;
        }
        await cancel({
          variables: {
            "refund_method": "prorata",
            "source": cancellationSource.toLowerCase(),
            "carrier_moved": carrierMovedTo,
            "description": cancellationReason,
            "effective_date": `${convertToUtc(effectiveDate)}`,
            "other_carrier": carrierMovedTo === 'Other' ? otherCarrier : '',
            "queryEnv": environment,
            "system_code": sourceSystemCode,
            "policy_number": policyNumber,
            "method": method,
            "cancellation_reason_code": reasonCode,
            "isCppProduct": isCppProduct,
            "isWorkerCompLine": isWorkerCompLine
          }
        });
        if (uploadAvailable) {
          const docData = await translateDocument(cancellationDocument);
          await upload({
            variables: {
              policy_number: policyNumber,
              file_name: cancellationDocument.name,
              mime_type: cancellationDocument.type,
              document_type: 'letter_received',
              security_type: securityType,
              source_system_code: sourceSystemCode,
              document_data: docData,
              query_env: environment
            }
          });
        }
      } catch (err) {
        if (err instanceof Error) {
          console.error('Error on Policy Cancellation:', err.message);
        } else {
          console.error('Error on Policy Cancellation:', err);
        }
      }
      // if (!cancelError && !uploadError && !cancelData?.uwReferral) {
      //   // await cancel({
      //   //   variables: {
      //   //     policy_number: policyNumber,
      //   //     method: 'confirmPolicyCancellation',
      //   //     system_code: systemCode,
      //   //     queryEnv: environment,
      //   //   }
      //   // });
      // }
      openCancellation = true;
      setShowPolicyInfoModel(true);
      setFormStatus(formStatusObj.submitted);
      setCancelDataUpdate(true);
    } else if (!acceptanceLoading) {
      setFormStatus(formStatusObj.submitted);
      openCancellation = true;
      setShowPolicyInfoModel(true);
    }
  };

  const startCancellation = async ({
    cancellationReason,
    effectiveDate,
    cancellationDocument,
    securityType,
    reasonCode,
    uploadAvailable,
    carrierMovedTo,
    sourceSystemCode,
    otherCarrier,
    refundMethod
  }: formValues) => {
    setFormStatus(formStatusObj.submitting);
    let cancelObj = {
      "policy_number": policyNumber,
      "source": cancellationSource.toLowerCase(),
      "description": cancellationReason,
      "cancellation_reason_code": reasonCode,
      "carrier_moved": carrierMovedTo,
      "other_carrier": carrierMovedTo === 'Other' ? otherCarrier : '',
      "effective_date": `${effectiveDate}`,
      "refund_method": refundMethod || 'prorata',
      "queryEnv": environment,
      "system_code": sourceSystemCode,
      "method": "createPolicyCancellationTransaction",
      "isCppProduct": false,
      "isWorkerCompLine": false
    };
    try {
      if ( sourceSystemCode === 'GWPL') {
        setRunSubscription(policyNumber);
        await cancel({ variables: cancelObj });
      } else {
        if (cppProducts.includes(product)) {
          cancelObj.isCppProduct = true
        }
        if (product === "WORKERS COMP"){
          cancelObj.isWorkerCompLine = true
        }
        if (uploadAvailable) {
          const docData = await translateDocument(cancellationDocument);
          await upload({
            variables: {
              policy_number: policyNumber,
              file_name: cancellationDocument.name,
              mime_type: cancellationDocument.type,
              document_type: 'letter_received',
              security_type: securityType,
              source_system_code: sourceSystemCode,
              document_data: docData,
              query_env: environment,
              token: uploadToken || ""
            }
          });
        }
        setRunSubscription(policyNumber);
        await cancel({ variables: cancelObj });
      }
    } catch (error) {
      console.error('Caught Error:', error);
      if (error instanceof ApolloError) {
        console.error('ApolloError:', error.message);
      } else {
        console.error('Unexpected Error:', error);
      }
    }


    //   if (uploadAvailable) {
    //     const docData = await translateDocument(cancellationDocument);
    //     await upload({
    //       variables: {
    //         policy_number: policyNumber,
    //         file_name: cancellationDocument.name,
    //         mime_type: cancellationDocument.type,
    //         document_type: 'letter_received',
    //         security_type: securityType,
    //         source_system_code: sourceSystemCode,
    //         document_data: docData,
    //         query_env: environment
    //       }
    //     });
    //   }
    // } catch (err) {
    //   if (err instanceof Error) {
    //     console.error('Error on Policy Cancellation:', err.message);
    //   } else {
    //     console.error('Error on Policy Cancellation:', err);
    //   }
    // }
    // // if (!cancelError && !uploadError && !cancelData?.uwReferral) {
    // //   // await cancel({
    // //   //   variables: {
    // //   //     policy_number: policyNumber,
    // //   //     method: 'confirmPolicyCancellation',
    // //   //     system_code: systemCode,
    // //   //     queryEnv: environment,
    // //   //   }
    // //   // });
    // // }
    // openCancellation = true;
    // setShowPolicyInfoModel(true);
    // setFormStatus(formStatusObj.submitted);
    // setCancelDataUpdate(true);

  };

  const confirmCancellationClick = async () => {
    try {
      await cancel({
        variables: {
          policy_number: policyNumber,
          method: 'confirmPolicyCancellation',
          system_code: systemCode,
          queryEnv: environment,
        }
      });
      setCancelDataUpdate(true);
    } catch (err) {
      if (err instanceof Error) {
        console.error('Error on Confirm Cancellation:', err.message);
      } else {
        console.error('Error on Confirm Cancellation:', err);
      }
    }
  };

  const handleErrorCase = ( message: string ) => {
    let errorMessage = "Quote cannot be completed at this time. Please close out this message and try your quote again. If you continue to have issues, please contact State Auto Customer Service at the following phone number: 800-288-4425."
    if ( message === "Cannot cancel policy: No period for this effective date" ) {
      errorMessage = message;
    }
    return errorMessage;
  };

  const addDelay = async () => {
    setIsCancelledSuccess(true);
    await new Promise(resolve => setTimeout(resolve, 5000));
    window.location.reload();
  };

  useEffect(() => {
    // confirm cancellation for PL Policy
    if (cancelData?.cancelPolicy?.status === 'success' && cancelData?.cancelPolicy?.message === 'cancellation_confirmed' ) {
      addDelay();
    };
    if (cancelData?.cancelPolicy?.status === 'error') {
      const errorText = handleErrorCase(cancelData?.cancelPolicy?.message || "");
      setErrorMessage(errorText);
      setShowError(true);
      setFormStatus(formStatusObj.notSubmitted);
    };
    if (subscriptionData && subscriptionData?.onProcessResult) {
      const cancelResponse = JSON.parse(subscriptionData?.onProcessResult?.message);
      if ( cancelResponse.status === "Quoted" && cancelResponse.type === "Cancellation") {
        window.location.reload();
      }
    }
  }, [subscriptionData, cancelData]);

  if (showError) {
    return (
      <AlertModal setError={setShowError} errorMessage={errorMessage.toUpperCase()} />
    );
  }

  const withdrawSuccessHandler = async () => {
    await new Promise(resolve => setTimeout(resolve, 2000));
    setShowPolicyInfoModel(false);
  };

  const showCancelPolicyButton = !isCancelled && !scheduledCancellation;
  const showCancelPolicyInfo = showPolicyInfoModel && openCancellation;

  if (isCancelledSuccess) {
    setIsUpdated(true);
    return (
      <div style={{ "display": "flex", "justifyContent": "space-between", "alignItems": "center" }}>
        <SuccessText>
          <AltSuccess />
          Your policy has been cancelled successfully. If changes are needed, please contact your Underwriter.
        </SuccessText>
      </div>
    );
  };

  // if (cancelData?.cancelPolicy?.status === "Error") {
  //   return (
  //     <CancellationRequestSent
  //       acceptable={acceptable}
  //       account_number={accountNumber}
  //       policy_number={policyNumber}
  //       systemCode={systemCode}
  //       confirmClickHandler={confirmClickHandler}
  //       query_env={environment}
  //       error={cancelError || uploadError}
  //       underwriting={cancelData?.uwReferral}
  //       result={result}
  //     />
  //   )
  // };

  // if (isCancellationStarted) {
  //   setStartReload(true);
  // };

  return (
    <div>
      {showCancelPolicyInfo ? (
        <CancelPolicyAccordionWrap>
          <CancelPolicyInfo
            account_number={accountNumber}
            policy_number={policyNumber}
            systemCode={systemCode}
            confirmClickHandler={confirmClickHandler}
            query_env={environment}
            result={result}
            withdrawSuccessHandler={withdrawSuccessHandler}
            confirmCancel={confirmCancellationClick}
            setIsUpdated={setIsUpdated}
            product={product}
            uploadToken={uploadToken}
          />
        </CancelPolicyAccordionWrap>
      ) : showCancelPolicyButton ? (
        <CancelPolicyAccordionWrap>
          <CancelPolicyAccordionHead
            disabled={false}
            onClick={() => {
              setIsOpen(!isOpen);
              if(!isOpen){
                getRefundMethodsForCPP();
              }
            }}
          >
            <HeaderIconWrap>
              <>
                {!showCancelPolicyFlag ? (
                  <CancelPolicyDisabled />
                ) : (
                  <HeaderIcon src={CancelPolicyIconSrc} />
                )}
              </>
            </HeaderIconWrap>
            <HeaderTitleWrap>
              <HeaderTitle>CANCEL THIS POLICY</HeaderTitle>
              <HeaderChevronWrap>
                {isOpen ? (
                  <ChevronUp />
                ) : (
                  <>{!showCancelPolicyFlag ? <ChevronDownDisabled /> : <ChevronDownUnbolded />}</>
                )}
              </HeaderChevronWrap>
            </HeaderTitleWrap>
          </CancelPolicyAccordionHead>
          <CancelPolicyAccordionBody isOpen={isOpen}>
            {formStatus !== formStatusObj.submitted ? (
              <CancelPolicyForm
                submit={submitForm}
                startCancellation={startCancellation}
                formStatus={formStatus}
                systemCode={systemCode}
                maxDate={maxDate}
                minDate={minDate}
                cancellationSource={cancellationSource}
                setCancellationSource={setCancellationSource}
                product={product}
                initialSource={initialSource}
                close={() => setIsOpen(false)}
                openChanges={openChanges}
                getAcceptance={getAcceptance}
                acceptable={acceptable}
                acceptanceLoading={acceptanceLoading}
                accountNumber={accountNumber}
                policyNumber={policyNumber}
                environment={environment}
                sourceSystemCode={sourceSystemCode}
                getRefundMethodClickHandler={getRefundMethodClickHandler}
                refundMethods={refundMethods}
                isFarmRanchLine={isFarmRanchLine}
              />
            ) : (
              <></>
            )}
          </CancelPolicyAccordionBody>
        </CancelPolicyAccordionWrap>
      ) : isCancelled ? (
        <div style={{ "display": "flex", "justifyContent": "space-between", "alignItems": "center" }}>
          <WarningText>This policy has been cancelled. If changes are needed, please contact your Underwriter.</WarningText>
        </div>
      ) : scheduledCancellation ? (
        <div style={{ "display": "flex", "justifyContent": "space-between", "alignItems": "center" }}>
          {(product === "WORKERS COMP") ? <WarningText> The Policy is pending cancellation. </WarningText> :
            <WarningText> The Policy is pending cancellation.  If changes are needed, please contact your Underwriter.</WarningText>
          }
        </div>
      ) : (
        <div style={{ "display": "flex", "justifyContent": "space-between", "alignItems": "center" }}>
          <WarningText> There is no data available at this moment, please try again later.</WarningText>
        </div>
      )}
    </div>
  );
};
